import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FontAwesomeIcon
} from '@fortawesome/react-fontawesome';
import {
  faPlay, faArrowLeft, faCheck, faCalendar, faStar, faFilm,
  faEye, faChevronDown, faSpinner, faChevronUp, faImage, faAngleDown,
  faThLarge, faList, faComments
} from '@fortawesome/free-solid-svg-icons';
import { ShowsAPI, CommentsAPI, PopularityAPI } from '../../api';
import { AppContext } from '../../context/AppContext';
import { AuthContext } from '../../context/AuthContext';
import ContentSlider from '../Home/ContentSlider';
import WatchlistButton from '../Watchlist/WatchlistButton';
import { formatRating } from '../../utils/formatters';
import { ShowComments } from '../Comments';

// Optimized CSS components
const DetailsContainer = styled.div`
  position: relative;
`;

const Backdrop = styled.div`
  height: 550px; // Increased height for better display
  background-image: ${props => `linear-gradient(to bottom, 
    rgba(0, 0, 0, 0.4) 0%, 
    rgba(0, 0, 0, 0.4) 40%, 
    rgba(0, 0, 0, 0.5) 60%,
    rgba(0, 0, 0, 0.6) 75%,
    rgba(0, 0, 0, 0.7) 85%,
    rgba(0, 0, 0, 0.8) 95%,
    rgba(12, 12, 12, 0.9) 100%), 
    url(${props.image})`};
  background-color: #111;
  background-size: cover;
  background-position: center 20%; // Better positioning
  position: relative;
  border-bottom: none;
  
  @media (max-width: 768px) {
    height: 350px;
    background-position: center 20%;
  }
`;

const FallbackBackdrop = styled.div`
  height: 450px;
  background: linear-gradient(to bottom, 
    rgba(20, 20, 20, 1) 0%, 
    rgba(22, 22, 22, 0.95) 40%, 
    rgba(18, 18, 18, 0.98) 70%,
    rgba(17, 17, 17, 0.99) 90%,
    var(--background-dark) 100%);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: none;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to top, 
      var(--background-dark) 0%, 
      rgba(18, 18, 18, 0.98) 5%,
      rgba(18, 18, 18, 0.95) 10%,
      rgba(18, 18, 18, 0.8) 20%,
      rgba(18, 18, 18, 0.5) 40%,
      rgba(18, 18, 18, 0.2) 70%,
      transparent 85%);
    pointer-events: none;
  }
  
  svg {
    font-size: 60px;
    color: #333;
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const BottomFade = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 250px; // Increased height for smoother gradient
  background: linear-gradient(to top, 
    var(--background-dark) 0%, 
    rgba(18, 18, 18, 0.98) 15%,
    rgba(18, 18, 18, 0.95) 30%,
    rgba(18, 18, 18, 0.85) 45%,
    rgba(18, 18, 18, 0.7) 60%,
    rgba(18, 18, 18, 0.5) 75%,
    rgba(18, 18, 18, 0.3) 85%,
    rgba(18, 18, 18, 0.1) 95%,
    transparent 100%);
  z-index: 1;
  pointer-events: none;
`;

const Content = styled.div`
  padding: 0 40px;
  margin-top: -220px;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: row;
  
  @media (max-width: 1024px) {
    padding: 0 30px;
    margin-top: -200px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 20px;
    margin-top: -150px;
  }
`;

const TopContent = styled.div`
  display: flex;
  width: 100%;
  
  @media (min-width: 769px) {
    display: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    margin-bottom: 15px;
  }
`;

const Poster = styled.div`
  width: 220px;
  height: 330px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.7);
  position: relative;
  transition: transform 0.3s ease;
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.8);
  }
  
  @media (max-width: 768px) {
    width: 120px;
    height: 180px;
    margin-bottom: 0;
    flex-shrink: 0;
  }
`;

const PosterDesktop = styled(Poster)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const PosterMobile = styled(Poster)`
  @media (min-width: 769px) {
    display: none;
  }
`;

const PosterImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  animation: imageFadeIn 0.5s ease-out forwards;
  
  @keyframes imageFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const FallbackPoster = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  color: #333;
  
  svg {
    font-size: 40px;
  }
`;

const Info = styled.div`
  flex: 1;
  padding: 10px 0 0 30px;
  
  @media (max-width: 768px) {
    padding: 10px 0 0 15px;
  }
`;

const InfoDesktop = styled(Info)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const InfoMobile = styled(Info)`
  @media (min-width: 769px) {
    display: none;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 15px;
  background: linear-gradient(to right, #fff, #aaa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 10px;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 15px;
  
  span {
    margin-right: 15px;
    margin-bottom: 8px;
    color: var(--text-dim);
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 5px;
      color: var(--primary-light);
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 10px;
    
    span {
      margin-right: 10px;
      margin-bottom: 5px;
      font-size: 13px;
    }
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
  
  span {
    background-color: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(5px);
    padding: 5px 10px;
    border-radius: 5px;
    margin-right: 10px;
    margin-bottom: 10px;
    font-size: 14px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    transition: all 0.3s ease;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
      transform: translateY(-2px);
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 15px;
    
    span {
      padding: 3px 8px;
      margin-right: 6px;
      margin-bottom: 6px;
      font-size: 12px;
    }
  }
`;

const Description = styled.p`
  margin-bottom: 30px;
  color: var(--text-dim);
  max-width: 800px;
  line-height: 1.7;
  font-size: 16px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
    font-size: 14px;
    line-height: 1.5;
    width: 100%;
  }
`;

const DescriptionDesktop = styled(Description)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const DescriptionMobile = styled(Description)`
  @media (min-width: 769px) {
    display: none;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 30px;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 10px;
    margin-bottom: 20px;
    justify-content: space-between;
  }
`;

const ActionsDesktop = styled(Actions)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const ActionsMobile = styled(Actions)`
  @media (min-width: 769px) {
    display: none;
  }
`;

const WatchButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
  flex: 1;
  min-width: 0;
  max-width: 180px;
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-3px);
    box-shadow: 0 7px 20px rgba(59, 130, 246, 0.5);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:disabled {
    background-color: #444;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    margin-right: 8px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
    flex: 1;
  }
`;

const ActionButtonWrapper = styled.div`
  flex: 1;
  max-width: 180px;
  min-width: 0;
  
  @media (max-width: 768px) {
    flex: 1;
  }
`;

const CommentButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--text-dim);
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 0;
  max-width: 180px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    color: var(--text-light);
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 6px;
    color: var(--text-dim);
    font-size: 13px;
    flex-shrink: 0;
  }
  
  &:hover svg {
    color: var(--primary-light);
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
    flex: 1;
  }
`;

const SeasonsContainer = styled.div`
  margin-top: 40px;
  padding: 0 40px;
  
  @media (max-width: 1024px) {
    padding: 0 30px;
  }
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const SeasonsHeader = styled.div`
  padding: 15px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    font-size: 24px;
    font-weight: 600;
    background: linear-gradient(to right, var(--primary-light), #fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const SeasonDropdown = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  margin: 20px 0;
`;

const SeasonSelector = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }
  
  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: #1a1a1a;
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  max-height: 300px;
  overflow-y: auto;
  z-index: 10;
  margin-top: 5px;
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transform: ${props => props.$open ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.3s ease;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #1a1a1a;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: var(--primary-light);
    border-radius: 3px;
  }
`;

const DropdownItem = styled.div`
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.$active ? 'rgba(59, 130, 246, 0.2)' : 'transparent'};
  
  &:hover {
    background-color: ${props => props.$active ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
`;

const EpisodesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  padding: 20px 0;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ListEpisodeItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  border-radius: 12px;
  background-color: #1a1a1a;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  overflow: hidden;
  
  ${props => !props.$isExpanded && `
    &:hover {
      background-color: #353a44;
    }
  `}
`;

const EpisodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 20px;
  transition: background-color 0.2s ease;
  
  ${props => !props.$isExpanded && `
    &:hover {
      background-color: #353a44;
    }
  `}
`;

const EpisodeInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ListEpisodeNumber = styled.div`
  color: #cccccc;
  margin-right: 10px;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const ListEpisodeTitle = styled.div`
  color: #e0e0e0;
  font-weight: 400;
  line-height: 1.4;
  
  span { 
    display: block;
    font-size: 16px; 
    margin-bottom: 4px;
  }
`;

const ExpandIcon = styled.div`
  color: #9ca3af;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  width: 20px;
  height: 20px;
`;

const EpisodeDescription = styled.div`
  padding: 0 20px 24px 20px;
  color: #cccccc;
  font-size: 14px;
  line-height: 1.5;
  animation: fadeIn 0.3s ease-out forwards;
  opacity: 0;
  background-color: #1a1a1a;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ViewMoreButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  margin: 20px auto;
  display: block;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-3px);
    box-shadow: 0 7px 20px rgba(59, 130, 246, 0.4);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:disabled {
    background-color: #444;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SimilarContent = styled.div`
  margin-top: 40px;
  padding-bottom: 50px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  svg {
    font-size: 40px;
    color: var(--primary-light);
    animation: spin 1s linear infinite;
  }
  
  p {
    margin-top: 15px;
    color: white;
    font-size: 16px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ImageErrorMessage = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 8px;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  color: var(--text-dim);
  font-size: 12px;
  opacity: ${props => props.$visible ? 0.8 : 0};
  transition: opacity 0.3s ease;
  pointer-events: none;
  backdrop-filter: blur(4px);
`;

const ViewToggleButton = styled.button`
  background-color: transparent;
  border: none;
  color: var(--text-light);
  font-size: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: 5px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  ${props => props.$active && `
    color: var(--primary-light);
    background-color: rgba(59, 130, 246, 0.1);
  `}
`;

const GridViewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  gap: 10px;
  padding: 20px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(45px, 1fr));
    gap: 8px;
  }
`;

const EpisodeGridItem = styled.div`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$watched ? 'rgba(75, 181, 67, 0.2)' : 'rgba(26, 26, 26, 0.8)'};
  border: 1px solid ${props => props.$watched ? 'rgba(75, 181, 67, 0.5)' : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.$watched ? 'rgba(75, 181, 67, 0.9)' : 'var(--text-color)'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    background-color: ${props => props.$watched ? 'rgba(75, 181, 67, 0.3)' : 'rgba(42, 42, 42, 0.8)'};
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const EpisodeGridImage = styled.div`
  position: relative;
  aspect-ratio: 16/9;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .episode-no-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #1a1a1a;
    color: #333;
    
    svg {
      font-size: 30px;
    }
  }
`;

const EpisodeGridPlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  svg {
    font-size: 30px;
    color: white;
  }
  
  ${EpisodeGridImage}:hover & {
    opacity: 1;
  }
`;

const EpisodeGridWatched = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(75, 181, 67, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const EpisodeGridNumber = styled.div`
  font-size: 13px;
  color: var(--primary-light);
  margin-bottom: 4px;
`;

const EpisodeGridTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-light);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EpisodesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
  padding: 20px 0;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 15px;
  }
`;

const ViewToggle = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 8px;
  color: var(--text-light);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  &.active {
    background-color: var(--primary-color);
  }
`;

const EpisodesLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 0;
  color: var(--text-dim);
  
  svg {
    font-size: 30px;
    margin-bottom: 15px;
    color: var(--primary-light);
  }
`;

const NoEpisodes = styled.div`
  text-align: center;
  padding: 50px 0;
  color: var(--text-dim);
  
  .message {
    display: flex;
    flex-direction: column;
    align-items: center;
    
    svg {
      font-size: 40px;
      margin-bottom: 15px;
      opacity: 0.5;
    }
    
    p {
      font-size: 16px;
    }
  }
`;

const EpisodeItem = styled.div`
  background-color: rgba(26, 26, 26, 0.7);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

const EpisodeMain = styled.div`
  display: flex;
  align-items: center;
`;

const EpisodeNumber = styled.div`
  color: var(--primary-light);
  font-weight: 600;
  font-size: 16px;
  margin-right: 12px;
`;

const EpisodeTitle = styled.div`
  color: var(--text-light);
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  
  &:hover {
    color: var(--primary-light);
  }
`;

const EpisodeActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const EpisodeWatchStatus = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$watched ? 'rgba(75, 181, 67, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$watched ? 'rgba(75, 181, 67, 0.9)' : 'var(--text-dim)'};
`;

const EpisodeToggle = styled.button`
  background: transparent;
  border: none;
  color: var(--text-dim);
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--primary-light);
    transform: scale(1.1);
  }
`;

const EpisodeDetails = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const EpisodeDetailImage = styled.div`
  width: 200px;
  height: 113px;
  border-radius: 8px;
  overflow: hidden;
  margin-right: 15px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    margin-right: 0;
    margin-bottom: 15px;
  }
`;

const EpisodePlay = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 20px auto 0;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 8px 16px;
  border-radius: 8px;
  color: var(--text-light);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  background-color: rgba(26, 26, 26, 0.8);
  color: var(--text-light);
  border: none;
  padding: 8px 15px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 15px;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.2);
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 8px;
    color: var(--text-dim);
  }
  
  @media (max-width: 768px) {
    position: fixed;
    top: 20px;
    left: 20px;
    z-index: 101;
    background-color: rgba(26, 26, 26, 0.9);
    backdrop-filter: blur(5px);
    border-radius: 50%;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    box-shadow: 0 3px 15px rgba(0, 0, 0, 0.4);
    
    span {
      display: none;
    }
    
    svg {
      margin-right: 0;
      font-size: 22px;
      color: var(--text-light);
    }
  }
`;

// Add a styled component for the comments section
const CommentsSection = styled.div`
  padding: 0 40px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 0;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--background-dark);
    z-index: 100;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
`;

const CommentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    padding: 15px 20px 15px 75px;
    background-color: rgba(18, 18, 18, 0.95);
    position: sticky;
    top: 0;
    z-index: 5;
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const CommentsTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: var(--text-light);
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CommentsContent = styled.div`
  @media (max-width: 768px) {
    padding: 0 20px 20px;
    flex: 1;
    margin-top: 100px; /* Increase from 80px to 100px to provide more space below the back button */
  }
`;

const EpisodeExpandedContent = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 0;
  animation: contentFadeIn 0.3s ease-out forwards;
  
  @keyframes contentFadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const EpisodeImageContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const EpisodeImageWrapper = styled.div`
  width: 80px;
  height: 120px;
  border-radius: 6px;
  overflow: hidden;
`;

const EpisodeImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  animation: imageFadeIn 0.5s ease-out forwards;
  
  @keyframes imageFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const EpisodeShowName = styled.div`
  margin-top: 8px;
  background-color: #0a0a0a;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  color: #cccccc;
  font-weight: 500;
  text-align: center;
`;

const EpisodeDetailsContainer = styled.div`
  flex: 1;
`;

const EpisodeDetailItem = styled.div`
  color: #ffffff;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 4px;
  animation: detailsFadeIn 0.4s ease-out forwards;
  
  @keyframes detailsFadeIn {
    from {
      opacity: 0;
      transform: translateY(5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const EpisodeWatchProgress = styled.div`
  color: #ffffff;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 16px;
  animation: detailsFadeIn 0.4s ease-out forwards;
`;

const ShowDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshUserData } = useContext(AppContext);
  const { currentUser } = useContext(AuthContext);

  // State
  const [show, setShow] = useState(null);
  const [similarShows, setSimilarShows] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [watchHistory, setWatchHistory] = useState([]);
  const [episodeWatchStatus, setEpisodeWatchStatus] = useState({});

  const [loading, setLoading] = useState(true);
  const [episodesLoading, setEpisodesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [error, setError] = useState(null);
  
  const [backdropError, setBackdropError] = useState(false);
  const [posterError, setPosterError] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [totalEpisodes, setTotalEpisodes] = useState(0);

  const [expandedEpisodes, setExpandedEpisodes] = useState({});

  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  
  // New state for comments
  const [activeTab, setActiveTab] = useState('show');
  const [commentCount, setCommentCount] = useState(0);
  const [loadComments, setLoadComments] = useState(false);
  
  // Add refs to prevent duplicate API calls
  const isLoadingShow = useRef(false);
  const isLoadingEpisodes = useRef(false);
  const hasTrackedView = useRef(false);
  const commentFetchTimeout = useRef(null);

  const activeSeason = useMemo(() => {
    if (!show || !selectedSeasonId) {
      return null;
    }
    return show.seasons.find(season => season._id === selectedSeasonId) || null;
  }, [show, selectedSeasonId]);

  // Load episodes directly using getAllSeasonEpisodes instead of paginated endpoint
  const loadAllEpisodes = useCallback(async (showId, seasonId) => {
    if (!showId || !seasonId || isLoadingEpisodes.current) return;
    
    try {
      isLoadingEpisodes.current = true;
      setEpisodesLoading(true);
      console.log(`Loading all episodes for show ${showId}, season ${seasonId}`);
      
      // Use the getAllSeasonEpisodes API endpoint to get all episodes at once
      const response = await ShowsAPI.getAllSeasonEpisodes(showId, seasonId);
      
      if (response.data && response.data.success) {
        const { data, watchStatus } = response.data;
        console.log(`Loaded ${data?.length || 0} episodes successfully`);
        
        if (data && Array.isArray(data) && data.length > 0) {
          setEpisodes(data);
          setTotalEpisodes(data.length);
          
          // Update watch status if available
          if (watchStatus) {
            setEpisodeWatchStatus(watchStatus);
          }
        } else {
          console.warn('API returned success but no episode data');
          setEpisodes([]);
          setTotalEpisodes(0);
        }
      } else {
        console.warn('Failed to load episodes or invalid response');
        setEpisodes([]);
        setTotalEpisodes(0);
      }
    } catch (err) {
      console.error("Error loading episodes:", err);
      setEpisodes([]);
      setTotalEpisodes(0);
    } finally {
      setEpisodesLoading(false);
      isLoadingEpisodes.current = false;
    }
  }, []);

  // Replace the old loadEpisodes function with this simpler version
  const loadEpisodes = useCallback(() => {
    if (!show || !selectedSeasonId) return;
    loadAllEpisodes(show._id, selectedSeasonId);
  }, [show, selectedSeasonId, loadAllEpisodes]);

  // Update useEffect to use the new loadEpisodes function
  useEffect(() => {
    if (!show || !selectedSeasonId) return;
    
    // Reset episode-related state
    setEpisodes([]);
    setExpandedEpisodes({});
    setEpisodeWatchStatus({});
    
    // Load episodes for the selected season
    loadEpisodes();
  }, [show, selectedSeasonId, loadEpisodes]);

  // Track content view with debounce
  const trackContentView = useCallback(async (contentType, contentId) => {
    if (hasTrackedView.current) return;
    
    try {
      hasTrackedView.current = true;
      await PopularityAPI.trackContentView(contentType, contentId);
      console.log('Content view tracked successfully');
    } catch (error) {
      console.error('Failed to track content view:', error);
    }
  }, []);

  useEffect(() => {
    const fetchShowDetails = async () => {
      // Prevent duplicate API calls
      if (isLoadingShow.current) return;
      
      isLoadingShow.current = true;
      setShow(null);
      setSelectedSeasonId(null);
      setEpisodes([]);
      setSimilarShows([]);
      setWatchHistory([]);
      setEpisodeWatchStatus({});
      setExpandedEpisodes({});
      setTotalEpisodes(0);
      setBackdropError(false);
      setPosterError(false);
      setLoading(true);
      setError(null);
      setEpisodesLoading(false);
      hasTrackedView.current = false;

      try {
        const response = await ShowsAPI.getById(id);
        
        // Check if response.data has nested data structure or is direct
        const showData = response.data.data || response.data;
        const relatedShows = response.data.related || [];
        const historyData = response.data.watchHistory || [];
        
        console.log("API Watch History:", historyData);
        
        // Validate show data
        if (!showData || !showData._id) {
          throw new Error('Invalid show data received from API');
        }
        
        setShow(showData);
        setSimilarShows(relatedShows);
        setWatchHistory(historyData); 

        if (showData.seasons && showData.seasons.length > 0) {
          setSelectedSeasonId(showData.seasons[0]._id);
        } else {
          setSelectedSeasonId(null);
        }

        // Track this show view using the new PopularityAPI with a small delay
        setTimeout(() => {
          trackContentView('show', id);
        }, 1000);
        
      } catch (err) {
        console.error("Error fetching show details:", err);
        setError('Failed to load show details. Please try again later.');
      } finally {
        setLoading(false);
        isLoadingShow.current = false;
      }
    };

    fetchShowDetails();
  }, [id, trackContentView]);

  // Add separate useEffect for comment count, with a slight delay and debounce
  useEffect(() => {
    // Only fetch comment count if show data is loaded
    if (!show) return;
    
    const fetchCommentCount = async () => {
      try {
        // Clear any existing timeout
        if (commentFetchTimeout.current) {
          clearTimeout(commentFetchTimeout.current);
        }
        
        // Set a new timeout for fetching comments
        commentFetchTimeout.current = setTimeout(async () => {
          const commentsResponse = await CommentsAPI.getShowComments(id, {
            limit: 1,
            count_only: true
          });
          
          if (commentsResponse.data && commentsResponse.data.data) {
            setCommentCount(commentsResponse.data.data.total || 0);
          }
          
          commentFetchTimeout.current = null;
        }, 2000); // 2 second delay
      } catch (commentErr) {
        console.error('Error fetching comment count:', commentErr);
      }
    };
    
    fetchCommentCount();
    
    // Cleanup function to clear timeout
    return () => {
      if (commentFetchTimeout.current) {
        clearTimeout(commentFetchTimeout.current);
      }
    };
  }, [show, id]);

  useEffect(() => {
    if (watchHistory && watchHistory.length > 0) {
      console.log("Watch history loaded:", watchHistory);
    }
  }, [watchHistory]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.season-dropdown')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const isEpisodeWatched = useCallback((episodeId) => {
    // Check if this episode exists in the episodeWatchStatus object
    return !!episodeWatchStatus[episodeId];
  }, [episodeWatchStatus]);

  // Add these functions AFTER isEpisodeWatched is defined
  const findFirstUnwatchedEpisode = useCallback(() => {
    if (!episodes || episodes.length === 0) return null;
    
    // Find the first episode that hasn't been watched
    const firstUnwatched = episodes.find(episode => !isEpisodeWatched(episode._id));
    
    // If all episodes are watched, return the first episode
    return firstUnwatched || episodes[0];
  }, [episodes, isEpisodeWatched]);

  // Add a function to check if any episodes in the current season have been watched
  const hasWatchedEpisodesInSeason = useCallback(() => {
    if (!episodeWatchStatus || Object.keys(episodeWatchStatus).length === 0) {
      return false;
    }
    
    // If any episodes in the current season have a watch status, return true
    return episodes.some(episode => isEpisodeWatched(episode._id));
  }, [episodes, episodeWatchStatus, isEpisodeWatched]);

  // Completely rebuild the handleWatchClick function
  const handleWatchClick = async () => {
    if (!show || !activeSeason) {
      alert('No show or season data available.');
      return;
    }
    
    if (episodesLoading) {
      alert('Episodes are still loading. Please wait a moment.');
      return;
    }
    
    if (!episodes || episodes.length === 0) {
      alert('No episodes available for this show or season.');
      return;
    }
    
    // Find the episode to play - first unwatched episode if continuing, or first episode if starting new
    const episodeToPlay = findFirstUnwatchedEpisode();
    
    if (!episodeToPlay || !episodeToPlay._id) {
      alert('Invalid episode data. Please try refreshing the page.');
      return;
    }

    try {
      setActionLoading(true);
      console.log(`Starting playback for episode: ${episodeToPlay._id}`);
      
      const episodeResponse = await ShowsAPI.getEpisodeById(episodeToPlay._id);
      
      // Check if the response has the expected structure
      if (!episodeResponse?.data?.data && !episodeResponse?.data) {
        console.error('Failed to load episode data - invalid response');
        alert('Failed to load episode data. Please try again.');
        return;
      }
      
      // Handle both response formats
      const fullEpisodeData = episodeResponse.data.data || episodeResponse.data;
      
      if (!fullEpisodeData.links || fullEpisodeData.links.length === 0) {
        alert('No video sources available for this episode.');
        return;
      }
      
      // Make sure we have valid links
      const validLinks = fullEpisodeData.links.filter(link => link && link.url && link.name);
      
      if (validLinks.length === 0) {
        throw new Error('No valid video sources found');
      }

      // Update watch status only if user is logged in
      if (currentUser) {
        await ShowsAPI.updateEpisodeWatchStatus(episodeToPlay._id, { progress: 0, completed: false })
          .catch(err => console.error("Failed to update watch status:", err));
        refreshUserData();
      }

      // Create navigation state
      const navigationState = {
        id: episodeToPlay._id,
        showId: show._id,
        type: 'episode',
        title: `${show.title} - S${activeSeason.seasonNumber}E${episodeToPlay.episodeNumber}: ${episodeToPlay.title || 'Episode ' + episodeToPlay.episodeNumber}`,
        sources: validLinks.map(link => ({ label: link.name, url: link.url })),
        description: episodeToPlay.description || '',
        show: show,
        season: activeSeason,
        episode: fullEpisodeData
      };

      // Log navigation attempt
      console.log('Navigating to player with state:', navigationState);

      // Navigate to player
      navigate('/player', {
        state: navigationState,
        replace: false // Ensure we don't replace the current history entry
      });
    } catch (err) {
      console.error('Error starting playback:', err);
      alert('Failed to start playback. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEpisodeClick = async (episode) => {
    if (!show || !activeSeason || !episode) {
      return;
    }
    
    try {
      setActionLoading(true);
      const episodeResponse = await ShowsAPI.getEpisodeById(episode._id);
      
      // Check if the response has the expected structure
      if (!episodeResponse?.data?.data && !episodeResponse?.data) {
        console.error('Failed to load episode data - invalid response');
        alert('Failed to load episode data. Please try again.');
        return;
      }
      
      // Handle both response formats
      const fullEpisodeData = episodeResponse.data.data || episodeResponse.data;

      if (!fullEpisodeData.links || fullEpisodeData.links.length === 0) {
        alert('No video sources available for this episode.');
        return;
      }
      
      // Make sure we have valid links
      const validLinks = fullEpisodeData.links.filter(link => link && link.url && link.name);
      
      if (validLinks.length === 0) {
        throw new Error('No valid video sources found');
      }

      // Update watch status only if user is logged in
      if (currentUser) {
        ShowsAPI.updateEpisodeWatchStatus(episode._id, { progress: 0, completed: false })
          .catch(err => console.error("Failed to update watch status:", err));
        refreshUserData();
      }

      // Navigate to player for all users regardless of login status
      navigate('/player', {
        state: {
          id: episode._id,
          showId: show._id,
          type: 'episode',
          title: `${show.title} - S${activeSeason.seasonNumber}E${episode.episodeNumber}: ${episode.title || 'Episode ' + episode.episodeNumber}`,
          sources: validLinks.map(link => ({ label: link.name, url: link.url })),
          description: episode.description || '',
          show: show,
          season: activeSeason,
          episode: fullEpisodeData
        }
      });
    } catch (err) {
      console.error('Error starting playback:', err);
      alert('Failed to start playback. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleEpisodeExpand = (e, episodeId) => {
    e.stopPropagation();
    setExpandedEpisodes(prev => ({
      ...prev,
      [episodeId]: !prev[episodeId]
    }));
  };

  const handleLoadMore = () => {
    console.log("Load more is not needed - all episodes are loaded at once");
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleSeasonSelect = (seasonId) => {
    if (selectedSeasonId !== seasonId) {
      setSelectedSeasonId(seasonId);
      setDropdownOpen(false);
    } else {
      setDropdownOpen(false);
    }
  };

  const handlePosterError = () => {
    setPosterError(true);
  };

  const handleBackdropError = () => {
    setBackdropError(true);
  };

  const toggleViewMode = () => {
    setViewMode(prev => prev === 'list' ? 'grid' : 'list');
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'comments') {
      setLoadComments(true);
    }
  };

  // Add this function to handle back button action
  const handleBackFromComments = () => {
    handleTabChange('show');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Add a state to track if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Add effect to update isMobile state when window resizes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  if (loading) {
    return (
      <LoadingOverlay>
        <LoadingSpinner>
          <FontAwesomeIcon icon={faSpinner} spin />
          <p>Loading show details...</p>
        </LoadingSpinner>
      </LoadingOverlay>
    );
  }

  if (error || !show) {
    return <div className="error-message" style={{ padding: '50px', textAlign: 'center', color: 'var(--text-color)' }}>{error || 'Show not found or failed to load.'}</div>;
  }

  // Only show comments in full screen if on mobile and comments tab is active
  if (isMobile && activeTab === 'comments') {
    return (
      <CommentsSection>
        <BackButton onClick={handleBackFromComments}>
          <FontAwesomeIcon icon={faArrowLeft} /> 
          <span>Back to Show</span>
        </BackButton>
        <CommentsContent>
          {loadComments && <ShowComments showId={id} />}
        </CommentsContent>
      </CommentsSection>
    );
  }

  const rating = formatRating(show.rating);
  const hasSeasons = show.seasons && show.seasons.length > 0;
  const backdropImageUrl = show.coverImage || show.image;
  const continueWatching = hasWatchedEpisodesInSeason();

  // Update the watchButtonDisabled condition to not depend on currentUser
  const watchButtonDisabled = 
    actionLoading || 
    !hasSeasons || 
    !activeSeason || 
    (episodesLoading && episodes.length === 0) || 
    (!episodes || episodes.length === 0);

  return (
    <DetailsContainer>
      {actionLoading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <FontAwesomeIcon icon={faSpinner} spin />
            <p>Preparing playback...</p>
          </LoadingSpinner>
        </LoadingOverlay>
      )}

      {backdropImageUrl && !backdropError ? (
        <Backdrop image={backdropImageUrl}>
          <BottomFade />
        </Backdrop>
      ) : (
        <FallbackBackdrop>
          <FontAwesomeIcon icon={faImage} />
          <BottomFade />
        </FallbackBackdrop>
      )}

      {/* Desktop Layout */}
      <Content>
        <PosterDesktop>
          {show.image && !posterError ? (
            <PosterImage
              src={show.image}
              alt={`${show.title} Poster`}
              onError={handlePosterError}
            />
          ) : (
            <FallbackPoster>
              <FontAwesomeIcon icon={faImage} />
            </FallbackPoster>
          )}
          <ImageErrorMessage $visible={posterError && !!show.image}>
            Image unavailable
          </ImageErrorMessage>
        </PosterDesktop>

        <InfoDesktop>
          <Title>{show.title}</Title>
          <Meta>
            {show.startYear && (
              <span>
                <FontAwesomeIcon icon={faCalendar} />
                {show.startYear}
                {show.endYear && show.endYear !== show.startYear ? ` - ${show.endYear}` : ''}
              </span>
            )}
            {show.status && <span><FontAwesomeIcon icon={faFilm} />{show.status}</span>}
            {rating !== 'N/A' && <span><FontAwesomeIcon icon={faStar} />{rating}</span>}
          </Meta>
          <Tags>
            {show.tags && show.tags.map((tag, index) => (<span key={index}>{tag}</span>))}
          </Tags>
          <DescriptionDesktop>{show.description || 'No description available.'}</DescriptionDesktop>
          <ActionsDesktop>
            <WatchButton onClick={handleWatchClick} disabled={watchButtonDisabled}>
              <FontAwesomeIcon icon={faPlay} /> {continueWatching ? 'Continue' : 'Watch Now'}
            </WatchButton>
            <ActionButtonWrapper>
              <WatchlistButton contentType="show" contentId={show._id} />
            </ActionButtonWrapper>
            <CommentButton onClick={() => handleTabChange('comments')}>
              <FontAwesomeIcon icon={faComments} /> 
              Comments {commentCount > 0 && `(${commentCount})`}
            </CommentButton>
          </ActionsDesktop>
        </InfoDesktop>

        {/* Mobile Layout */}
        <TopContent>
          <PosterMobile>
            {show.image && !posterError ? (
              <PosterImage
                src={show.image}
                alt={`${show.title} Poster`}
                onError={handlePosterError}
              />
            ) : (
              <FallbackPoster>
                <FontAwesomeIcon icon={faImage} />
              </FallbackPoster>
            )}
            <ImageErrorMessage $visible={posterError && !!show.image}>
              Image unavailable
            </ImageErrorMessage>
          </PosterMobile>

          <InfoMobile>
            <Title>{show.title}</Title>
            <Meta>
              {show.startYear && (
                <span>
                  <FontAwesomeIcon icon={faCalendar} />
                  {show.startYear}
                  {show.endYear && show.endYear !== show.startYear ? ` - ${show.endYear}` : ''}
                </span>
              )}
              {show.status && <span><FontAwesomeIcon icon={faFilm} />{show.status}</span>}
              {rating !== 'N/A' && <span><FontAwesomeIcon icon={faStar} />{rating}</span>}
            </Meta>
            <Tags>
              {show.tags && show.tags.map((tag, index) => (<span key={index}>{tag}</span>))}
            </Tags>
          </InfoMobile>
        </TopContent>
        <DescriptionMobile>{show.description || 'No description available.'}</DescriptionMobile>
        <ActionsMobile>
          <WatchButton onClick={handleWatchClick} disabled={watchButtonDisabled}>
            <FontAwesomeIcon icon={faPlay} /> {continueWatching ? 'Continue' : 'Watch'}
          </WatchButton>
          <ActionButtonWrapper>
            <WatchlistButton contentType="show" contentId={show._id} />
          </ActionButtonWrapper>
          <CommentButton onClick={() => handleTabChange('comments')}>
            <FontAwesomeIcon icon={faComments} /> 
            Comments
          </CommentButton>
        </ActionsMobile>
      </Content>

      {activeTab === 'comments' && !isMobile ? (
        <CommentsSection>
          <BackButton onClick={handleBackFromComments}>
            <FontAwesomeIcon icon={faArrowLeft} /> 
            <span>Back to Show</span>
          </BackButton>
          <CommentsContent>
            {loadComments && <ShowComments showId={id} />}
          </CommentsContent>
        </CommentsSection>
      ) : (
        <>
          {hasSeasons && (
            <SeasonsContainer>
              <SeasonsHeader>
                <h2>Episodes</h2>
                <ViewToggleButton 
                  onClick={toggleViewMode} 
                  $active={viewMode === 'grid'}
                  aria-label={viewMode === 'list' ? 'Switch to grid view' : 'Switch to list view'}
                >
                  <FontAwesomeIcon icon={viewMode === 'list' ? faThLarge : faList} />
                </ViewToggleButton>
              </SeasonsHeader>

              {show.seasons.length > 1 ? (
                <SeasonDropdown className="season-dropdown"> 
                  <SeasonSelector onClick={toggleDropdown} $open={dropdownOpen}>
                    <span>
                      {activeSeason ? `Season ${activeSeason.seasonNumber}` : 'Select Season'}
                    </span>
                    <FontAwesomeIcon icon={faAngleDown} />
                  </SeasonSelector>
                  <DropdownMenu $open={dropdownOpen}>
                    {show.seasons.map(season => (
                      <DropdownItem
                        key={season._id}
                        $active={selectedSeasonId === season._id}
                        onClick={() => handleSeasonSelect(season._id)}
                      >
                        Season {season.seasonNumber}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </SeasonDropdown>
              ) : show.seasons.length === 1 && activeSeason ? (
                <div style={{ margin: '20px 0', color: 'var(--text-color)', fontSize: '18px', paddingLeft: '15px' }}>
                  Season {activeSeason.seasonNumber}
                </div>
              ) : null}

              {selectedSeasonId && activeSeason ? (
                <>
                  {viewMode === 'list' ? (
                    <EpisodesList>
                      {episodesLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '30px', gridColumn: '1 / -1', minHeight: '150px' }}>
                          <FontAwesomeIcon icon={faSpinner} spin size="2x" color="var(--primary-light)" />
                        </div>
                      ) : episodes.length > 0 ? (
                        episodes.map(episode => {
                          // Check if this episode has been watched using the episodeWatchStatus object
                          const watched = isEpisodeWatched(episode._id);
                          
                          return (
                            <ListEpisodeItem
                              key={episode._id}
                              onClick={() => handleEpisodeClick(episode)}
                              role="button"
                              tabIndex="0"
                              onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && handleEpisodeClick(episode)}
                              aria-label={`Play Season ${activeSeason.seasonNumber} Episode ${episode.episodeNumber}: ${episode.title}`}
                              $isExpanded={!!expandedEpisodes[episode._id]}
                            >
                              <EpisodeHeader $isExpanded={!!expandedEpisodes[episode._id]}>
                                <EpisodeInfo>
                                  <ListEpisodeNumber>
                                    S{activeSeason.seasonNumber} E{episode.episodeNumber}
                                  </ListEpisodeNumber>
                                  <ListEpisodeTitle>
                                    <span>{episode.title || `Episode ${episode.episodeNumber}`}</span> 
                                    {watched && ( 
                                      <div style={{ fontSize: '12px', color: 'rgba(75, 181, 67, 0.9)', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
                                        <FontAwesomeIcon icon={faCheck} /> {episodeWatchStatus[episode._id]?.progress ? `${episodeWatchStatus[episode._id].progress}% watched` : 'Watched'}
                                      </div>
                                    )}
                                  </ListEpisodeTitle>
                                </EpisodeInfo>
                                  <ExpandIcon
                                      onClick={(e) => toggleEpisodeExpand(e, episode._id)}
                                      role="button"
                                      tabIndex="0"
                                      onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && toggleEpisodeExpand(e, episode._id)}
                                      aria-expanded={!!expandedEpisodes[episode._id]}
                                      aria-controls={`episode-desc-${episode._id}`}
                                    aria-label={expandedEpisodes[episode._id] ? 'Collapse details' : 'Expand details'}
                                  >
                                      <FontAwesomeIcon icon={expandedEpisodes[episode._id] ? faChevronUp : faChevronDown} />
                                  </ExpandIcon>
                              </EpisodeHeader>
                              {expandedEpisodes[episode._id] && (
                                <EpisodeDescription 
                                  id={`episode-desc-${episode._id}`}
                                  onAnimationEnd={(e) => {
                                    e.target.style.animation = '';
                                  }}
                                >
                                  <EpisodeExpandedContent>
                                    <EpisodeImageContainer>
                                      <EpisodeImageWrapper>
                                        <EpisodeImage 
                                          src={episode.image || "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=120&h=160&fit=crop&crop=faces"}
                                          alt={episode.title}
                                        />
                                      </EpisodeImageWrapper>
                                    </EpisodeImageContainer>
                                    
                                    <EpisodeDetailsContainer>
                                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <div>
                                          {activeSeason && (
                                            <EpisodeDetailItem>
                                              Season {activeSeason.seasonNumber}
                                            </EpisodeDetailItem>
                                          )}
                                          {episode.durationMinutes && (
                                            <EpisodeDetailItem>
                                              {episode.durationMinutes} min
                                            </EpisodeDetailItem>
                                          )}
                                          {episodeWatchStatus[episode._id] && (
                                            <EpisodeWatchProgress>
                                              {episodeWatchStatus[episode._id].progress || 0}% watched
                                            </EpisodeWatchProgress>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {episode.description && (
                                        <div 
                                          style={{ 
                                            color: '#cccccc', 
                                            fontSize: '14px', 
                                            lineHeight: '1.5', 
                                            marginTop: '0',
                                            animation: 'detailsFadeIn 0.4s ease-out forwards'
                                          }}
                                        >
                                          {episode.description}
                                        </div>
                                      )}
                                    </EpisodeDetailsContainer>
                                  </EpisodeExpandedContent>
                                </EpisodeDescription>
                              )}
                            </ListEpisodeItem>
                          );
                        })
                      ) : (
                        !episodesLoading && (
                          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-dim)', gridColumn: '1 / -1' }}>
                            No episodes found for Season {activeSeason.seasonNumber}.
                          </div>
                        )
                      )}
                    </EpisodesList>
                  ) : (
                    <GridViewContainer>
                      {episodesLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '30px', gridColumn: '1 / -1', minHeight: '150px' }}>
                          <FontAwesomeIcon icon={faSpinner} spin size="2x" color="var(--primary-light)" />
                        </div>
                      ) : episodes.length > 0 ? (
                        episodes.map(episode => {
                          const watched = isEpisodeWatched(episode._id);
                          return (
                            <div
                              key={episode._id}
                              onClick={() => handleEpisodeClick(episode)}
                              style={{
                                aspectRatio: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: watched ? 'rgba(75, 181, 67, 0.2)' : 'rgba(26, 26, 26, 0.8)',
                                border: `1px solid ${watched ? 'rgba(75, 181, 67, 0.5)' : 'rgba(255, 255, 255, 0.05)'}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontSize: '16px',
                                fontWeight: 600,
                                color: watched ? 'rgba(75, 181, 67, 0.9)' : 'var(--text-color)',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                              }}
                              role="button"
                              tabIndex="0"
                              onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && handleEpisodeClick(episode)}
                              aria-label={`Play Season ${activeSeason.seasonNumber} Episode ${episode.episodeNumber}`}
                              title={episode.title || `Episode ${episode.episodeNumber}`}
                            >
                              {episode.episodeNumber}
                            </div>
                          );
                        })
                      ) : (
                        !episodesLoading && (
                          <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-dim)', gridColumn: '1 / -1' }}>
                            No episodes found for Season {activeSeason.seasonNumber}.
                          </div>
                        )
                      )}
                    </GridViewContainer>
                  )}
                </>
              ) : (
                !episodesLoading && (
                  <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-dim)' }}>
                    {show.seasons && show.seasons.length > 0 ? 'Please select a season to view episodes.' : 'No seasons currently available for this show.'}
                  </div>
                )
              )}
            </SeasonsContainer>
          )}
          {!hasSeasons && (
             <SeasonsContainer>
               <SeasonsHeader>
                   <h2>Episodes</h2>
               </SeasonsHeader>
               <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-dim)'}}>
                   No seasons or episodes available for this show.
               </div>
           </SeasonsContainer>
          )}

          {similarShows && similarShows.length > 0 ? (
            <SimilarContent>
              <ContentSlider title="You Might Also Like" items={similarShows} type="show" />
            </SimilarContent>
          ) : (
            <SimilarContent style={{padding: '0 40px 50px 40px', marginTop: '40px'}}>
              <h2 style={{ 
                fontSize: '24px', 
                fontWeight: '600', 
                marginBottom: '20px', 
                background: 'linear-gradient(to right, var(--primary-light), #fff)', 
                WebkitBackgroundClip: 'text', 
                MozBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                MozTextFillColor: 'transparent',
                textFillColor: 'transparent'
              }}>
                You Might Also Like
              </h2>
              <p style={{ color: 'var(--text-dim)' }}>No similar shows found at the moment.</p>
            </SimilarContent>
          )}
        </>
      )}
    </DetailsContainer>
  );
};

export default ShowDetails;